import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_DONE = "done";
const STATUS_FAILED = "failed";


function FixVariantSkusTool() {
  const [inputFile, setInputFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: "/shopify/fix-variant-skus/jobs",
    cacheKey: "fixVariantSkus.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Fix Variant SKUs",
    description:
      "Upload a product sheet whose Variant SKU values are wrong (e.g. the SKU builder added an extra prefix). The tool looks each row up on Shopify by Title and rewrites the Variant SKU column to whatever Shopify actually has, so the corrected file can be reused for tag updates or re-imported.",
    steps: [
      "Upload a CSV or XLSX. Required columns: 'Product Title' (also accepts 'Title', 'Name', or 'Product Name') and a SKU column (accepts headers 'Variant SKU', 'SKU', or 'Parent SKU').",
      "Optional but recommended: include an 'Option1 Value' column. For multi-variant products it disambiguates which variant's SKU to pull.",
      "Click Start. You'll get a job ID and live progress.",
      "Leave the page if you like — jobs run server-side and reappear here when you return.",
      "When done, download the corrected workbook. The 'Product Sheet' tab has the original rows with Variant SKU rewritten where matches were found. The 'Fix Report' tab tells you per-row what was matched (or why it wasn't).",
    ],
    templates: [],
  };

  const guideTips = [
    "Match scope: only Shopify products with status=active are considered. Drafts and archived products won't be found.",
    "Multi-variant disambiguation: if a product has more than one variant, the tool needs Option1 Value (case-insensitive match against variant title / selectedOptions). Rows without a matchable Option1 Value are flagged 'ambiguous' instead of guessing.",
    "Fuzzy title matches (where Shopify's title search returned a product whose title doesn't exactly equal yours) are flagged in the Message column — always spot-check those before reusing the file.",
    "Only rows with status=fixed actually get the SKU column rewritten in the output sheet. no_change rows keep their original (already-correct) SKU. not_found / ambiguous / error rows keep their original SKU too — fix them manually using the Fix Report and run again.",
    "Workflow tip: once SKUs are corrected, run the corrected file through the Tag Update tool with 'SKU column' selected to delete tags from the matched products.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prev = previous[j.job_id];
      if (prev && prev !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(`Fix Variant SKUs job ${j.job_id} complete. File is ready.`, "success");
        } else if (j.status === STATUS_FAILED) {
          addToast(
            `Fix Variant SKUs job ${j.job_id} failed: ${j.error || "unknown error"}`,
            "error"
          );
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(jobs.map((j) => [j.job_id, j.status]));
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!inputFile) {
      addToast("Please upload an input file.", "error");
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("input_file", inputFile);

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post("/shopify/fix-variant-skus", formData, {
        onUploadProgress: (evt) => {
          const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
          setUploadProgress(pct);
        },
      });
      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      addToast(`Fix Variant SKUs job ${jobId} queued. Tracking progress…`, "info");
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setInputFile(null);
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to start Fix Variant SKUs job.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/shopify/fix-variant-skus/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "variant_skus_fixed.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Corrected file downloaded.", "success");
    } catch (error) {
      console.error(error);
      const message = await extractErrorMessage(error, "Failed to download corrected file.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Fix Variant SKUs"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Input File</h3>
              </div>
              <ToolFileUploadField
                label="Product sheet"
                file={inputFile}
                setFile={setInputFile}
                accept=".csv,.xlsx,.xls"
                helperText="CSV or XLSX with TITLE + Variant SKU columns. Optional: Option1 Value (needed for multi-variant products)."
              />
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={uploading}>
                {uploading ? "Uploading…" : "Start Fix Job"}
              </button>
              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading file…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Fix Variant SKUs Jobs"
          emptyText="No jobs yet. Submit a file above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
                const progress = j.progress || {};
                const total = Number(progress.total) || 0;
                const processed = Number(progress.processed) || 0;
                const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
                return (
                  <div key={j.job_id} className="tool-job-status">
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Job ID</span>
                      <span className="tool-job-value">{j.job_id}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Input</span>
                      <span className="tool-job-value">{j.input_filename || "—"}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Status</span>
                      <span className={`tool-job-value tool-job-status-${j.status}`}>
                        {j.status}
                      </span>
                    </div>
                    {total > 0 ? (
                      <>
                        <div className="tool-job-status-row">
                          <span className="tool-job-label">Progress</span>
                          <span className="tool-job-value">
                            {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                          </span>
                        </div>
                        <div className="progress-bar-container">
                          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                      </>
                    ) : null}
                    <div className="tool-job-counts">
                      <span>Fixed: {progress.fixed || 0}</span>
                      <span>No change: {progress.no_change || 0}</span>
                      <span>Not found: {progress.not_found || 0}</span>
                      <span>Ambiguous: {progress.ambiguous || 0}</span>
                      <span>Errors: {progress.errors || 0}</span>
                      <span>Skipped: {progress.skipped || 0}</span>
                    </div>
                    {j.status === STATUS_FAILED && j.error ? (
                      <div className="tool-job-error">Error: {j.error}</div>
                    ) : null}
                    {j.status === STATUS_DONE ? (
                      <button
                        type="button"
                        className="tool-btn-primary"
                        onClick={() => handleDownload(j)}
                        disabled={downloadingJobId === j.job_id}
                      >
                        {downloadingJobId === j.job_id ? "Downloading…" : "Download Corrected File"}
                      </button>
                    ) : null}
                  </div>
                );
              }}
            />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default FixVariantSkusTool;
